import React, { useEffect, useState } from 'react';

const defaultSearchClass =
  'w-full mb-1.5 px-2.5 py-1.5 rounded-md bg-black border border-gray-700 text-xs text-gray-200 placeholder:text-gray-600 focus:outline-none focus:ring-1 focus:ring-amber-300/40 focus:border-amber-300/40';

/**
 * Collapsible sidebar section. Auto-opens when a child card is active.
 * Optional search shown inside the expanded panel (e.g. Safety Forms).
 * If the section has only one card, clicking the header opens that view directly.
 */
export function CollapsibleSidebarNav({
  label,
  cards = [],
  activeView,
  onSelect,
  defaultOpen = false,
  className = '',
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search…',
  searchInputClass = defaultSearchClass,
  emptyText = 'No items.',
}) {
  const hasActiveChild = cards.some((card) => card.id === activeView);
  const isSingleCard = cards.length === 1;
  const singleCard = isSingleCard ? cards[0] : null;
  const [open, setOpen] = useState(defaultOpen || hasActiveChild);
  const showSearch = typeof onSearchChange === 'function';

  useEffect(() => {
    if (hasActiveChild && !isSingleCard) setOpen(true);
  }, [hasActiveChild, isSingleCard]);

  const handleHeaderClick = () => {
    if (isSingleCard && singleCard) {
      onSelect(singleCard.id);
      return;
    }
    setOpen((prev) => !prev);
  };

  const headerActive = isSingleCard
    ? activeView === singleCard?.id
    : open || hasActiveChild;

  return (
    <div className={`mb-3 shrink-0 ${className}`}>
      <button
        type="button"
        onClick={handleHeaderClick}
        aria-expanded={isSingleCard ? undefined : open}
        className={`w-full flex items-center justify-between gap-2 text-left px-3 py-2 rounded-md text-sm font-medium transition-colors border ${
          headerActive
            ? 'bg-gray-800 border-gray-700 text-white'
            : 'bg-gray-900/60 border-gray-800 text-gray-200 hover:bg-gray-900 hover:text-white'
        }`}
      >
        <span className="min-w-0 break-words text-xs font-semibold uppercase tracking-wide">
          {isSingleCard ? singleCard.title || label : label}
        </span>
        <span
          className={`shrink-0 text-base leading-none text-gray-300 transition-transform ${
            isSingleCard ? '' : open ? 'rotate-90' : ''
          }`}
          aria-hidden="true"
        >
          ›
        </span>
      </button>

      {!isSingleCard && open ? (
        <div className="mt-1.5 pl-0.5">
          {showSearch ? (
            <input
              type="search"
              value={searchValue || ''}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              className={searchInputClass}
            />
          ) : null}
          <nav className="space-y-0.5">
            {!cards.length ? (
              <p className="text-xs text-gray-500 px-2 py-2">{emptyText}</p>
            ) : (
              cards.map((card) => (
                <button
                  key={card.id}
                  type="button"
                  onClick={() => onSelect(card.id)}
                  className={`w-full flex items-center justify-between gap-2 text-left px-4 py-2 rounded-md text-sm transition-colors ${
                    activeView === card.id
                      ? 'bg-gray-800 text-white'
                      : 'text-gray-400 hover:bg-gray-900 hover:text-white'
                  }`}
                >
                  <span className="min-w-0 break-words">{card.title}</span>
                  <span
                    className="ml-1 shrink-0 text-base leading-none text-gray-300"
                    aria-hidden="true"
                  >
                    ›
                  </span>
                </button>
              ))
            )}
          </nav>
        </div>
      ) : null}
    </div>
  );
}
